package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.InstallPrompt
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.InstallFabActionStyle
import com.htmlagent.site.theme.InstallFabDismissStyle
import com.htmlagent.site.theme.InstallFabStyle
import com.htmlagent.site.theme.labelled
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event

/** How far down the offer waits: one screenful, which is the hero and nothing else. */
private const val APPEAR_AFTER_SCREENS = 1.0

/**
 * The offer to install HAL, floating at the corner of the page.
 *
 * It used to be a pill in the bar, which is the most expensive place on the site: the row had
 * 68 px of air left and this was 112 of them. An offer is not a destination and does not belong
 * in a row of destinations — and here it costs the layout nothing at all.
 *
 * It exists only while the browser offers to install, it waits for the hero to scroll past
 * because an offer to install something means nothing to somebody who has not seen it yet, and
 * it can be sent away. For the rest of the load: the browser offers this once per page, so
 * remembering the refusal beyond it would be remembering more than the prompt itself lasts.
 */
@Composable
fun InstallFab() {
    val t = LocalStrings.current
    val installable by InstallPrompt.available.collectAsState()
    var dismissed by remember { mutableStateOf(false) }
    var scrolledPast by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = {
            scrolledPast = window.scrollY > window.innerHeight * APPEAR_AFTER_SCREENS
        }
        // Read once on mount: the page can already be scrolled after a reload.
        scrolledPast = window.scrollY > window.innerHeight * APPEAR_AFTER_SCREENS
        window.addEventListener("scroll", listener)
        onDispose { window.removeEventListener("scroll", listener) }
    }

    // Wrapped in an `if` and not guarded by an early return: a `@Composable` that returns early
    // produces nothing, and goes on producing nothing after the state flips.
    if (installable && scrolledPast && !dismissed) {
        Div(InstallFabStyle.toModifier().toAttrs()) {
            Button(
                attrs = InstallFabActionStyle.toModifier().toAttrs {
                    attr("type", "button")
                    onClick { InstallPrompt.prompt() }
                },
            ) {
                Glyph(Icons.Download)
                Text(t.landing.navInstall)
            }
            Button(
                attrs = InstallFabDismissStyle.toModifier()
                    .labelled(t.landing.installDismiss)
                    .toAttrs {
                        attr("type", "button")
                        onClick { dismissed = true }
                    },
            ) {
                Glyph(Icons.Close)
            }
        }
    }
}
